// learningInteractionService — the dual-write target during Phase 0 of the
// Learning Interaction Framework rollout (see INTERACTION_FRAMEWORK.md).
//
// Every interactive slide submit is also written here, behind the
// fk_learning_interactions tenant feature flag. The service is intentionally
// thin — it does only what the framework specifies:
//   1. Validate the response payload's top-level shape against the registry's
//      responseSchema (required keys + primitive type only).
//   2. Look up the cognitive level snapshot from the slide config (with the
//      registry's default as fallback).
//   3. Compute correctness + score via the registry's answerKey when graded.
//   4. Compute mastery_signal via lib/masterySignal.ts.
//   5. Upsert one row per (enrollment_id, client_id).
//
// All other framework-specified semantics (offline queue, retention, partitioning)
// land in later phases.

import { supabase } from '../lib/supabase';
import type { Slide, SlideData } from '../components/course-builder/slides/slideTypes';
import {
  findRegistryBySlide,
  type InteractionMeta,
  type InteractionVerb,
  type InteractionTier,
  type ResponseSchema,
} from '../components/course-builder/slides/slideRegistry';
import {
  MASTERY_SIGNAL_VERSION,
  computeMasterySignal,
  type Correctness,
  type MasterySignal,
} from '../lib/masterySignal';

/** Tenant flag cache — checked once per session unless explicitly busted. */
let _featureFlagCache: { tenantId: string; enabled: boolean } | null = null;

export function bustFeatureFlagCache(): void {
  _featureFlagCache = null;
}

/**
 * Reads tenants.feature_flags.fk_learning_interactions for the current user's
 * tenant. Cached for the session. Returns false on any error — the dual-write
 * is best-effort and must never break the primary write path.
 */
export async function isLearningInteractionsEnabled(): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data: userRow } = await supabase
      .from('users')
      .select('tenant_id')
      .eq('id', user.id)
      .maybeSingle();
    if (!userRow?.tenant_id) return false;

    if (_featureFlagCache?.tenantId === userRow.tenant_id) {
      return _featureFlagCache.enabled;
    }

    // Intentional DB-truth read for the fk_ kill-switch: this deliberately
    // skips the getFeatures() cascade (DEFAULT_FEATURES → env overrides) so
    // the flag can ONLY be flipped via tenants.feature_flags — an instant,
    // env-independent kill switch for the dual-write.
    const { data: tenantRow } = await supabase
      .from('tenants')
      .select('feature_flags')
      .eq('id', userRow.tenant_id)
      .maybeSingle();

    const enabled = Boolean(
      (tenantRow?.feature_flags as Record<string, unknown> | null)?.fk_learning_interactions,
    );
    _featureFlagCache = { tenantId: userRow.tenant_id, enabled };
    return enabled;
  } catch {
    return false;
  }
}

/** Public payload shape passed by callers. Matches framework cells 1:1. */
export interface WriteInteractionInput {
  enrollmentId: string;
  lessonId: string;
  slide: Slide;
  /** Stable identifier within the slide (e.g. 'q', a prediction id, a question id). */
  activityKey: string;
  verb: InteractionVerb;
  /** Learner's response payload. Validated against registry.interaction.responseSchema. */
  response: Record<string, unknown> | undefined;
  /** Optional — only set on graded slides with an answer key. */
  correctness?: Correctness;
  /** 0-1 normalised. Null/undefined for exploration. */
  score?: number | null;
  attemptNumber?: number;
  timeOnTaskMs?: number | null;
  /** Idempotency key — same value retried = same row. */
  clientId?: string;
  /** Defaults to Date.now(). */
  clientTs?: Date;
  /**
   * Phase 9 §9c — FK into experiences. Set by the lesson viewer when the
   * slide belongs to a scenario group whose experience row has been
   * resolved (via useLessonExperiences). Null/omitted for stand-alone
   * slides or when the fk_experiences_table flag is off.
   */
  experienceId?: string | null;
}

export interface WriteInteractionResult {
  written: boolean;
  /** Reason a write was skipped (e.g. 'flag-off', 'no-interaction-meta', 'validation-failed'). */
  skipReason?: string;
}

/**
 * Write a single learning_interactions row. Best-effort: returns
 * `{ written: false, skipReason }` on flag-off / validation failure rather than
 * throwing, so the primary activity_responses write is never blocked.
 */
export async function writeInteraction(
  input: WriteInteractionInput,
): Promise<WriteInteractionResult> {
  if (!(await isLearningInteractionsEnabled())) {
    return { written: false, skipReason: 'flag-off' };
  }

  const entry = findRegistryBySlide(input.slide);
  const meta = entry?.interaction;
  if (!meta) return { written: false, skipReason: 'no-interaction-meta' };

  if (!validateResponse(input.response, meta.responseSchema)) {
    return { written: false, skipReason: 'validation-failed' };
  }

  const tier: InteractionTier =
    typeof meta.tier === 'function' ? meta.tier(input.slide.data) : meta.tier;

  // Resolve cognitive level: slide override wins, else the registry default.
  const cognitiveLevel =
    input.slide.data.cognitiveLevel ?? meta.defaultCognitiveLevel ?? null;

  const correctness: Correctness =
    tier === 'exploration'
      ? 'n/a'
      : input.correctness ?? 'n/a';

  const masterySignal: MasterySignal = computeMasterySignal(correctness, cognitiveLevel);

  // Resolve tenant via the enrollment (RLS-safe — user owns the enrollment).
  const { data: enrollmentRow, error: enrollmentErr } = await supabase
    .from('enrollments')
    .select('tenant_id')
    .eq('id', input.enrollmentId)
    .maybeSingle();
  if (enrollmentErr || !enrollmentRow?.tenant_id) {
    return { written: false, skipReason: 'enrollment-not-found' };
  }

  const row = {
    tenant_id: enrollmentRow.tenant_id,
    enrollment_id: input.enrollmentId,
    lesson_id: input.lessonId,
    slide_id: input.slide.id,
    slide_revision: input.slide.revision ?? 1,
    activity_key: input.activityKey,
    template: input.slide.data.template,
    tier,
    verb: input.verb,
    response: input.response ?? null,
    correctness,
    score: input.score ?? null,
    // TODO(attempts): attempt_number is hard-coded to 1 unless a caller computes
    // it. Honest attempt tracking needs the real count of prior rows for
    // (enrollment_id, slide_id, activity_key) before each write. We don't do it
    // inline because the dual-write is a best-effort, fire-and-forget path and a
    // per-write SELECT would add latency to every interaction. Move this into the
    // upsert RPC (increment server-side) or a batched backfill when attempts
    // graduate from "nice to have" to a reported metric.
    attempt_number: input.attemptNumber ?? 1,
    time_on_task_ms: input.timeOnTaskMs ?? null,
    cognitive_level: cognitiveLevel,
    mastery_signal: masterySignal,
    client_ts: (input.clientTs ?? new Date()).toISOString(),
    client_id: input.clientId ?? null,
    // Phase 9 §9c — null when the slide isn't in an experience group, when
    // the flag is off, or when the resolver hasn't loaded yet. The column
    // is nullable; gradebook rollups filter by `experience_id IS NOT NULL`.
    experience_id: input.experienceId ?? null,
    meta: { masteryFormulaVersion: MASTERY_SIGNAL_VERSION },
  };

  // Upsert by (enrollment_id, client_id) for offline idempotency; falls back to
  // plain insert when client_id is absent.
  if (input.clientId) {
    const { error } = await supabase
      .from('learning_interactions')
      .upsert(row, { onConflict: 'enrollment_id,client_id' });
    if (error) return { written: false, skipReason: `upsert-error:${error.code}` };
  } else {
    const { error } = await supabase.from('learning_interactions').insert(row);
    if (error) return { written: false, skipReason: `insert-error:${error.code}` };
  }

  return { written: true };
}

/**
 * Minimal response shape check. We don't ship a full JSON-Schema validator —
 * we only enforce required top-level keys + primitive types. Anything more
 * elaborate is the renderer's responsibility (it produced the shape).
 */
function validateResponse(
  value: unknown,
  schema: ResponseSchema,
): boolean {
  if (schema.type === 'object') {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
    if (schema.required) {
      for (const key of schema.required) {
        if (!(key in (value as Record<string, unknown>))) return false;
      }
    }
    return true;
  }
  if (schema.type === 'array') return Array.isArray(value);
  if (schema.type === 'string') return typeof value === 'string';
  if (schema.type === 'number') return typeof value === 'number';
  if (schema.type === 'integer') return Number.isInteger(value);
  if (schema.type === 'boolean') return typeof value === 'boolean';
  return false;
}

/**
 * Helper: compute correctness + score from a graded slide's answer key.
 * Returns { correctness: 'n/a' } for exploration tier or when answerKey is missing.
 *
 * Each registry entry decides what to compare. The current matchers cover the
 * existing 8 entries' shapes; new templates that need bespoke scoring should
 * extend this function rather than the renderer.
 */
export function scoreResponse(
  meta: InteractionMeta,
  slide: SlideData,
  activityKey: string,
  response: Record<string, unknown> | undefined,
): { correctness: Correctness; score: number | null } {
  const tier = typeof meta.tier === 'function' ? meta.tier(slide) : meta.tier;
  if (tier !== 'graded' || !meta.answerKey || !response) {
    return { correctness: 'n/a', score: null };
  }

  const answers = meta.answerKey(slide);
  const expected = answers[activityKey];
  if (expected === undefined || expected === null) {
    return { correctness: 'n/a', score: null };
  }

  // Single-choice (quiz / dropdown / reveal-predict): expected is a string id.
  // Response carries `optionId` (quiz/dropdown) or `chosen` (reveal-predict).
  if (typeof expected === 'string') {
    const chosen = (response.optionId ?? response.chosen) as string | undefined;
    if (chosen === undefined) return { correctness: 'n/a', score: null };
    return chosen === expected
      ? { correctness: 'correct', score: 1 }
      : { correctness: 'incorrect', score: 0 };
  }

  // Self-scored (revise-run's `__results` summary row): the response carries
  // its own 0–1 score and is not a right-or-wrong answer, so correctness stays
  // n/a while the score is kept for reporting.
  if (expected && typeof expected === 'object' && !Array.isArray(expected) && (expected as { selfScored?: unknown }).selfScored === true) {
    const s = response.score;
    return typeof s === 'number' && Number.isFinite(s)
      ? { correctness: 'n/a', score: Math.max(0, Math.min(1, s)) }
      : { correctness: 'n/a', score: null };
  }

  // Any-of (revise-run either/or memos): expected is { anyOf: string[] }, the
  // response carries one `optionId`, and any listed id earns the mark. This
  // is deliberately NOT the string[] shape below — that means "all of these".
  if (expected && typeof expected === 'object' && !Array.isArray(expected) && Array.isArray((expected as { anyOf?: unknown }).anyOf)) {
    const anyOf = (expected as { anyOf: string[] }).anyOf;
    const chosen = response.optionId as string | undefined;
    if (chosen === undefined) return { correctness: 'n/a', score: null };
    return anyOf.includes(chosen)
      ? { correctness: 'correct', score: 1 }
      : { correctness: 'incorrect', score: 0 };
  }

  // String[] expected — two response shapes:
  //   - response.order (ranking): order-sensitive equality
  //   - response.found (spot-the-error): set equality (order irrelevant)
  if (Array.isArray(expected) && expected.every((e) => typeof e === 'string')) {
    const expectedArr = expected as string[];

    if (Array.isArray(response.order)) {
      const order = response.order as string[];
      const allRight = order.length === expectedArr.length
        && order.every((id, idx) => id === expectedArr[idx]);
      if (allRight) return { correctness: 'correct', score: 1 };
      const someRight = order.some((id, idx) => id === expectedArr[idx]);
      return someRight ? { correctness: 'partial', score: 0.5 } : { correctness: 'incorrect', score: 0 };
    }

    // Set-equality: spot-the-error uses `found`; claim-evidence uses
    // `selectedEvidence`. Both are set-shape (order doesn't matter).
    const setResponse = Array.isArray(response.found)
      ? (response.found as string[])
      : Array.isArray(response.selectedEvidence)
        ? (response.selectedEvidence as string[])
        : null;
    if (setResponse) {
      const expectedSet = new Set(expectedArr);
      const foundSet = new Set(setResponse);
      // Correct = found everything in the expected set AND nothing extra.
      const allCaught = expectedArr.every((e) => foundSet.has(e));
      const noFalsePositives = [...foundSet].every((f) => expectedSet.has(f));
      if (allCaught && noFalsePositives) return { correctness: 'correct', score: 1 };
      const correctlyFound = expectedArr.filter((e) => foundSet.has(e)).length;
      if (correctlyFound === 0) return { correctness: 'incorrect', score: 0 };
      return {
        correctness: 'partial',
        score: correctlyFound / expectedArr.length,
      };
    }

    return { correctness: 'n/a', score: null };
  }

  // Placement set (drag-sort): expected is Array<{ itemId, zoneId, position }>,
  // response.placements is Array<{ itemId, zoneId, position? }>. An item is
  // correct when its zone matches AND (when the key pins a position) its
  // position matches. `position: null` in the key means zone-only (categorise
  // mode); sequence mode pins an integer position.
  if (
    Array.isArray(expected) &&
    expected.length > 0 &&
    expected.every(
      (e) => e !== null && typeof e === 'object' && !Array.isArray(e) && 'itemId' in (e as object),
    )
  ) {
    const key = expected as Array<{ itemId: string; zoneId: string; position: number | null }>;
    const placements = Array.isArray(response.placements)
      ? (response.placements as Array<{ itemId: string; zoneId: string; position?: number }>)
      : null;
    if (!placements) return { correctness: 'n/a', score: null };
    const byItem = new Map(placements.map((p) => [p.itemId, p]));
    let correct = 0;
    for (const k of key) {
      const got = byItem.get(k.itemId);
      if (!got) continue;
      const zoneOk = got.zoneId === k.zoneId;
      const posOk = k.position == null || got.position === k.position;
      if (zoneOk && posOk) correct++;
    }
    if (correct === key.length) return { correctness: 'correct', score: 1 };
    if (correct === 0) return { correctness: 'incorrect', score: 0 };
    return { correctness: 'partial', score: correct / key.length };
  }

  // Pair set (matching): expected is Array<[left, right]>, response.pairs is too.
  if (Array.isArray(expected) && expected.every((e) => Array.isArray(e))) {
    const pairs = response.pairs as Array<[string, string]> | undefined;
    if (!Array.isArray(pairs)) return { correctness: 'n/a', score: null };
    const expectedSet = new Set(expected.map((p: unknown[]) => `${p[0]}|${p[1]}`));
    const matches = pairs.filter((p) => expectedSet.has(`${p[0]}|${p[1]}`)).length;
    if (matches === expected.length && matches === pairs.length) {
      return { correctness: 'correct', score: 1 };
    }
    return matches > 0
      ? { correctness: 'partial', score: matches / expected.length }
      : { correctness: 'incorrect', score: 0 };
  }

  return { correctness: 'n/a', score: null };
}

/**
 * Read-side helper for bridge slides (Phase 5b/d, fixup).
 *
 * Fetches the most recent learning_interactions row for a given slide and the
 * current authenticated user's enrollment, returning the response payload that
 * the responseRecap summarisers consume. Used as a fallback when the
 * in-session response cache misses (page refresh, returning learner).
 *
 * Resolves enrollmentId itself from the (userId, courseId) pair so callers
 * don't have to thread it. Returns null on any failure — bridges then fall
 * back to designer-narrative-only rendering, never throw.
 *
 * RLS ensures we only see the caller's own rows; no admin override here.
 */
export async function fetchPriorSlideResponse(
  slideId: string,
  courseId: string,
): Promise<Record<string, unknown> | null> {
  try {
    const enabled = await isLearningInteractionsEnabled();
    if (!enabled) return null;

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    // Resolve enrollment for (user, course). RLS will keep this scoped.
    const { data: enrollment } = await supabase
      .from('enrollments')
      .select('id')
      .eq('user_id', user.id)
      .eq('course_id', courseId)
      .maybeSingle();
    if (!enrollment?.id) return null;

    const { data, error } = await supabase
      .from('learning_interactions')
      .select('response, server_ts')
      .eq('slide_id', slideId)
      .eq('enrollment_id', enrollment.id)
      .order('server_ts', { ascending: false })
      .limit(1);

    if (error || !data || data.length === 0) return null;

    const single = data[0]?.response as Record<string, unknown> | null | undefined;
    return single ?? null;
  } catch {
    return null;
  }
}
